import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { format } from 'date-fns';
import { InputWithAlert } from '../ui/input-with-alert';
import { Combobox } from '../ui/combobox';
import { DatePicker } from '../ui/date-picker';
import { COUNTRIES_LIST, validateEmail, validateOnlyLatin, validatePesel, validatePhone } from '@/constants';
import { transformFromIdValueToValueLabel } from '@/constants';
import SignUpProgressBar from './SignUpProgressBar';
import { naturalClientSignup } from '@/service/weexpertService';
import { electronicFormatIBAN, isValidIBAN } from 'ibantools';
import SignupBankAccount from './SignupBankAccount';
import { Link } from 'react-router-dom';

const FORM_STORAGE_KEY = 'signup_form_data';

export default function SignUpForm() {
  const { t } = useTranslation(['signup', 'common', 'clients']);
  const [currentStage, setCurrentStage] = useState(1);
  const [inputChecked, setInputChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Stage 1 - Personal Information
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [passport, setPassport] = useState('');
  const [pesel, setPesel] = useState('');
  const [citizenship, setCitizenship] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState(null);

  // Stage 2 - Address Information
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');

  // Stage 3 - Bank Accounts
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountErrors, setBankAccountErrors] = useState(true);

  // Helper function to validate age
  const validateAge = (birthDate) => {
    if (!birthDate) return false;
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1 >= 19;
    } else {
      return age >= 19;
    }
  };

  // Load saved form data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(FORM_STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setCurrentStage(parsedData.currentStage || 1);
        setEmail(parsedData.email || '');
        setFirstName(parsedData.firstName || '');
        setLastName(parsedData.lastName || '');
        setPhone(parsedData.phone || '');
        setPassport(parsedData.passport || '');
        setPesel(parsedData.pesel || '');
        setCitizenship(parsedData.citizenship || '');
        setDateOfBirth(parsedData.dateOfBirth ? new Date(parsedData.dateOfBirth) : null);
        setAddress(parsedData.address || '');
        setPostcode(parsedData.postcode || '');
        setCity(parsedData.city || '');
        setBankAccounts(parsedData.bankAccounts || []);
        setInputChecked(false); // Reset validation state when loading saved data
      } catch (error) {
        console.error('Failed to load saved form data:', error);
      }
    }
    setIsLoading(false); // Allow saving after initial load is complete
  }, []);

  // Save form data to localStorage
  useEffect(() => {
    if (!isLoading) { // Only save after initial load is complete
      const formData = {
        currentStage,
        email,
        firstName,
        lastName,
        phone,
        passport,
        pesel,
        citizenship,
        dateOfBirth: dateOfBirth ? dateOfBirth.toISOString() : null,
        address,
        postcode,
        city,
        bankAccounts
      };
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    }
  }, [isLoading, currentStage, email, firstName, lastName, phone, passport, pesel, citizenship, dateOfBirth, address, postcode, city, bankAccounts]);

 const validateStage1 = () => {
  const emailValid = email && email.includes('@');
  const firstNameValid = firstName.trim().length > 0 && validateOnlyLatin(firstName);
  const lastNameValid = lastName.trim().length > 0 && validateOnlyLatin(lastName);
  const phoneValid = validatePhone(phone);
  const passportValid = passport.trim().length > 0 && validateOnlyLatin(passport);
  const citizenshipValid = citizenship && (typeof citizenship === 'string' ? citizenship.length > 0 : citizenship.value && citizenship.value.length > 0);
  const dateOfBirthValid = dateOfBirth !== null;
  const ageValid = validateAge(dateOfBirth);
  const peselValid = !pesel || validatePesel(pesel);

  return (
    emailValid &&
    firstNameValid &&
    lastNameValid &&
    phoneValid &&
    passportValid &&
    citizenshipValid &&
    dateOfBirthValid &&
    ageValid &&
    peselValid
  );
};

const validateStage2 = () => {
  const addressValid = address.trim().length > 0 && validateOnlyLatin(address);
  const postcodeValid = postcode.trim().length > 0 && validateOnlyLatin(postcode);
  const cityValid = city.trim().length > 0 && validateOnlyLatin(city);

  return addressValid && postcodeValid && cityValid;
};

  const validateStage3 = () => {
    return !bankAccountErrors && bankAccounts.length > 0;
  };

 const handleNext = () => {
  setInputChecked(true);

  if (currentStage === 1) {
    if (!validateStage1()) return;
    setCurrentStage(2);
    setInputChecked(false);
  } else if (currentStage === 2) {
    if (!validateStage2()) return;
    const allFormData = {
      personalInfo: {
        email,
        firstName,
        lastName,
        phone,
        passport,
        pesel,
        citizenship,
        dateOfBirth: dateOfBirth ? format(dateOfBirth, 'yyyy-MM-dd') : null
      },
      addressInfo: {
        address,
        postcode,
        city
      }
    };
    console.log('Form data after stage 2:', allFormData);
    setCurrentStage(3);
    setInputChecked(false);
  } else if (currentStage === 3 && validateStage3()) {
    handleSubmit();
  }
};

  const handlePrevious = () => {
    if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
      setInputChecked(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    // Format bank accounts for backend
    const formattedBankAccounts = bankAccounts
      .filter(account => account.account && account.account.trim().length > 0)
      .map(account => ({
        account: electronicFormatIBAN(account.account),
        currency: account.currency,
        swift: account.swift
      }));

    const signupData = {
      email,
      firstName,
      lastName,
      phone,
      passport: passport || undefined,
      pesel: pesel || undefined,
      citizenship: typeof citizenship === 'string' ? citizenship : citizenship?.value,
      dateOfBirth: dateOfBirth ? format(dateOfBirth, 'yyyy-MM-dd') : null,
      address,
      postcode,
      city,
      bankAccounts: formattedBankAccounts
    };

    try {
      const response = await naturalClientSignup(signupData);

      if (response.data.success) {
        setSubmitSuccess(true);
        localStorage.removeItem(FORM_STORAGE_KEY);

        // Show success message for a few seconds
        setTimeout(() => {
          // Redirect to login page or show login instructions
          window.location.href = '/';
        }, 5000);
      } else {
        setSubmitError(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Signup error:', error);

      if (error.response?.data?.errors) {
        setSubmitError('Please check your information and try again');
      } else if (error.response?.data?.message) {
        setSubmitError(error.response.data.message);
      } else {
        setSubmitError('Registration failed. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">{t('signup:sign_up')}</h1>

      <SignUpProgressBar currentStage={currentStage} totalStages={3} />

      <div className="mt-6 sm:mt-8">
        {currentStage === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-6">{t('signup:personal_information')}</h2>

            <InputWithAlert
              label={t('signup:email') + '*'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={inputChecked && (!email.trim() || !validateEmail(email))}
              errorMessage={
                !email.trim()
                  ? t('signup:empty_data_invalid')
                  : t('signup:email_invalid')
              }
              placeholder={t('signup:enter_email')}
              type="email"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
              <InputWithAlert
                label={t('signup:first_name') + '*'}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={
                  inputChecked &&
                  (!firstName.trim() || !validateOnlyLatin(firstName))
                }
                errorMessage={
                  !firstName.trim()
                    ? t('signup:empty_data_invalid')
                    : t('signup:only_latin_invalid')
                }
                placeholder={t('signup:enter_first_name')}
              />
              </div>

              <div>
               <InputWithAlert
                label={t('signup:last_name') + '*'}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={
                  inputChecked &&
                  (!lastName.trim() || !validateOnlyLatin(lastName))
                }
                errorMessage={
                  !lastName.trim()
                    ? t('signup:empty_data_invalid')
                    : t('signup:only_latin_invalid')
                }
                placeholder={t('signup:enter_last_name')}
              />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('signup:phone') + '*'}
              </label>
              <PhoneInput
                country={'pl'}
                value={phone}
                onChange={(value) => setPhone(value)}
                inputStyle={{ paddingLeft: '50px', width: '100%', height: '48px', border: '1px solid #e7e7e7' }}
                buttonStyle={{ width: '40px', height: '48px', background: 'inherit', border: '1px solid #e7e7e7' }}
                dropdownStyle={{ maxHeight: '200px', overflowY: 'scroll', border: '1px solid #e7e7e7', borderRadius: '6px' }}
                searchStyle={{ borderRadius: '5px', border: '1px solid black' }}
              />
              {inputChecked && !validatePhone(phone) && (
                <p className="text-red-500 text-sm mt-1">{t('signup:phone_invalid')}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <InputWithAlert
                  label={t('signup:passport') + '*'}
                  value={passport}
                  onChange={(e) => setPassport(e.target.value)}
                  error={
                    inputChecked &&
                    (!passport.trim() || !validateOnlyLatin(passport))
                  }
                  errorMessage={
                    !passport.trim()
                      ? t('signup:empty_data_invalid')
                      : t('signup:only_latin_invalid')
                  }
                  placeholder={t('signup:enter_passport')}
                />
              </div>

              <div>
                <InputWithAlert
                  label={t('signup:pesel')}
                  value={pesel}
                  onChange={(e) => setPesel(e.target.value)}
                  placeholder={t('signup:enter_pesel')}
                  error={inputChecked && !!pesel && !validatePesel(pesel)}
                  errorMessage={t('signup:pesel_invalid')}
                />
              </div>
            </div>

            <Combobox
              className={'w-full'}
              label={t('signup:citizenship') + '*'}
              value={citizenship}
              placeholder={t('signup:select_country')}
              error={inputChecked && !(citizenship && (typeof citizenship === 'string' ? citizenship.length > 0 : citizenship.value && citizenship.value.length > 0))}
              errorMessage={t('empty_data_invalid')}
              options={COUNTRIES_LIST.map(transformFromIdValueToValueLabel).map((item) => ({
                ...item,
                content: item,
                value: item.label,
              }))}
              onChange={(newValue) => setCitizenship(newValue)}
            />

            <DatePicker
              date={dateOfBirth}
              setDate={(e) => setDateOfBirth(e)}
              error={inputChecked && (!dateOfBirth || (dateOfBirth && !validateAge(dateOfBirth)))}
              label={t('signup:date_of_birth') + "*"}
              errorMessage={!dateOfBirth ? t('empty_data_invalid') : !validateAge(dateOfBirth) ? t('signup:age_invalid') : ''}
              inputClassName={'h-12 text-base'}
              disabled={(date) => {
                // Calculate the date 19 years ago from today
                const today = new Date();
                const nineteenYearsAgo = new Date(today.getFullYear() - 19, today.getMonth(), today.getDate());
                // Disable dates after the date 19 years ago (user must be at least 19)
                return date > nineteenYearsAgo;
              }}
              toDate={new Date(new Date().getFullYear() - 19, new Date().getMonth(), new Date().getDate())}
            />
          </div>
        )}

        {currentStage === 2 && (
          <div className="">
            <h2 className="text-xl font-semibold mb-6">{t('signup:address_information')}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4">
              <div>
                <InputWithAlert
                  label={t('signup:address') + '*'}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  error={
                    inputChecked &&
                    (!address.trim() || !validateOnlyLatin(address))
                  }
                  errorMessage={
                    !address.trim()
                      ? t('signup:empty_data_invalid')
                      : t('signup:only_latin_invalid')
                  }
                  placeholder={t('signup:enter_address')}
                />
              </div>

              <div>
                <InputWithAlert
                  label={t('signup:postcode') + '*'}
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  error={
                    inputChecked &&
                    (!postcode.trim() || !validateOnlyLatin(postcode))
                  }
                  errorMessage={
                    !postcode.trim()
                      ? t('signup:empty_data_invalid')
                      : t('signup:only_latin_invalid')
                  }
                  placeholder={t('signup:enter_postcode')}
                />
              </div>
            </div>

            <InputWithAlert
              label={t('signup:city') + '*'}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              error={
                inputChecked &&
                (!city.trim() || !validateOnlyLatin(city))
              }
              errorMessage={
                !city.trim()
                  ? t('signup:empty_data_invalid')
                  : t('signup:only_latin_invalid')
              }
              placeholder={t('signup:enter_city')}
            />
          </div>
        )}

        {currentStage === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-6">{t('signup:bank_account_information')}</h2>

            <SignupBankAccount
              bankAccounts={bankAccounts}
              setBankAccounts={setBankAccounts}
              onValidationChange={setBankAccountErrors}
              inputChecked={inputChecked}
            />
          </div>
        )}

        {submitSuccess && (
          <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
            <h3 className="font-semibold">{t('signup:registration_successful')}</h3>
            <p>{t('signup:registration_message')}</p>
            <p>{t('signup:registration_email_password')}</p>
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
            <h3 className="font-semibold">{t('signup:registration_failed')}</h3>
            <p>{t('signup:registration_error_message')}</p>
          </div>
        )}

        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:justify-between">
          {currentStage == 1 && (
            <Link className="text-sky-500 pt-2 block mr-3" to={'/'}>{t('login:alredy_have_account_login')}</Link>
          )}
          {currentStage > 1 && (
            <button
              onClick={handlePrevious}
              className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 order-2 sm:order-1"
            >
              {t('signup:previous')}
            </button>
          )}

          <button
            onClick={handleNext}
            disabled={submitSuccess || (currentStage === 3 && isSubmitting)}
            className={`w-full sm:w-auto sm:ml-auto px-6 py-2 rounded-md order-1 sm:order-2 ${
              submitSuccess || (currentStage === 3 && isSubmitting)
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {currentStage === 3 && isSubmitting
              ? 'Submitting...'
              : currentStage === 3
              ? t('signup:submit')
              : t('signup:next')
            }
          </button>
        </div>
      </div>
    </div>
  );
}
